import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouterState } from '@tanstack/react-router'
import { NpsDialog, npsCategory } from '@wehunt/ui'
import { useNpsEligibility } from './useNpsEligibility'
import { useSubmitNpsFeedback } from './useSubmitNpsFeedback'
import { useNpsPromptTiming } from './useNpsPromptTiming'
import { trackNpsPromptDismissed, trackNpsPromptShown, trackNpsPromptSubmitted } from './npsTracking'

type Phase =
  | { kind: 'survey' }
  | { kind: 'thanks'; score: number; canAddFeedback: boolean }
  | { kind: 'feedback'; score: number }

/**
 * The NPS prompt for WeHunt web.
 *
 * Eligibility (who) comes from the server; timing (when) from
 * useNpsPromptTiming. This component owns only the card's phases.
 *
 * The promoter path deliberately BUFFERS the score instead of writing it
 * immediately. A feedbacksHistory entry has no id, so a comment added afterwards
 * cannot patch the record it belongs to — writing twice would count as two
 * responses and would make the 60-day cooldown read the comment-only entry. So
 * the score is held until the user either adds a comment or declines, and
 * exactly one record is written either way.
 */
export function ChronicleNpsPrompt() {
  const { t } = useTranslation('nps')
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { eligible, daysSinceRegistration, daysSinceLatestSubmit } = useNpsEligibility()
  const { shouldShow, markShown } = useNpsPromptTiming({ eligible, pathname })
  const { submit, dismiss } = useSubmitNpsFeedback()
  const [phase, setPhase] = useState<Phase | null>(null)
  const shownAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (shouldShow && phase === null) {
      setPhase({ kind: 'survey' })
      markShown()
      shownAtRef.current = Date.now()
      trackNpsPromptShown({
        daysSinceFirstLogin: daysSinceRegistration,
        daysSinceLastFeedback: daysSinceLatestSubmit,
      })
    }
  }, [shouldShow, phase, markShown, daysSinceRegistration, daysSinceLatestSubmit])

  if (phase === null) return null

  // How long the card was on screen — the number CAMP-75's dismiss analysis
  // needs (mobile's dismiss population hesitates ~22 seconds).
  const secondsShown = () =>
    shownAtRef.current === null ? 0 : Math.round((Date.now() - shownAtRef.current) / 1000)

  const followUpTitle = (score: number) => {
    const category = npsCategory(score)
    if (category === 'detractor') return t('detractorPrompt')
    if (category === 'passive') return t('passivePrompt')
    return t('promoterPrompt')
  }

  const strings = {
    title: t('title'),
    question: t('question'),
    sentimentTexts: {
      detractor: `${t('unhappy')}\n${t('notRecommend')}`,
      passive: `${t('neutral')}\n${t('mayRecommend')}`,
      promoter: `${t('happy')}\n${t('recommend')}`,
    },
    followUpPlaceholder: t('hint'),
    skipLabel: t('back'),
    nextLabel: t('submit'),
    submitLabel: t('submit'),
    notLikelyLabel: t('notLikely'),
    veryLikelyLabel: t('veryLikely'),
  }

  if (phase.kind === 'survey') {
    return (
      <NpsDialog
        {...strings}
        followUpTitle={t('detractorPrompt')}
        onCancel={({ score, step, method }) => {
          // Skipping the COMMENT step still leaves a score worth recording;
          // leaving the SCORE step does not. NpsDialog owns that transition, so
          // the step arrives from it.
          if (step === 'feedback' && score !== null) {
            submit({ rate: score, description: undefined })
            trackNpsPromptSubmitted({ score, hasComment: false })
          } else {
            dismiss()
            trackNpsPromptDismissed({ method, secondsShown: secondsShown() })
          }
          setPhase(null)
        }}
        onSubmit={(score, feedback) => {
          // NpsDialog routes detractors and passives through its own comment
          // step, so a score arriving here from one of them is already final.
          if (npsCategory(score) !== 'promoter') {
            submit({ rate: score, description: feedback })
            trackNpsPromptSubmitted({ score, hasComment: feedback !== undefined })
            setPhase({ kind: 'thanks', score, canAddFeedback: false })
            return
          }
          setPhase({ kind: 'thanks', score, canAddFeedback: true })
        }}
      />
    )
  }

  if (phase.kind === 'feedback') {
    return (
      <NpsDialog
        {...strings}
        initialStep="feedback"
        initialScore={phase.score}
        highlightOnOpen={false}
        followUpTitle={followUpTitle(phase.score)}
        onCancel={() => {
          // Declining the comment still records the score they gave. This phase
          // always mounts on the comment step, so no step check is needed.
          submit({ rate: phase.score, description: undefined })
          trackNpsPromptSubmitted({ score: phase.score, hasComment: false })
          setPhase(null)
        }}
        onSubmit={(score, feedback) => {
          submit({ rate: score, description: feedback })
          trackNpsPromptSubmitted({ score, hasComment: feedback !== undefined })
          setPhase({ kind: 'thanks', score, canAddFeedback: false })
        }}
      />
    )
  }

  const closeThanks = () => {
    if (phase.canAddFeedback) {
      submit({ rate: phase.score, description: undefined })
      trackNpsPromptSubmitted({ score: phase.score, hasComment: false })
    }
    setPhase(null)
  }

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 12px 12px 18px',
        borderRadius: 12,
        background: '#2b2f2c',
        color: '#fff',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
      }}
    >
      <span style={{ fontSize: 14 }}>{t('thankYou')}</span>
      {phase.canAddFeedback && (
        <button
          type="button"
          onClick={() => setPhase({ kind: 'feedback', score: phase.score })}
          style={{
            border: 'none',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.12)',
            color: '#9fd48a',
            fontSize: 13,
            fontWeight: 700,
            padding: '8px 12px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {t('addFeedback')}
        </button>
      )}
      <button
        type="button"
        onClick={closeThanks}
        aria-label={t('dismiss')}
        style={{
          border: 'none',
          background: 'transparent',
          color: '#fff',
          fontSize: 16,
          lineHeight: 1,
          padding: 8,
          cursor: 'pointer',
        }}
      >
        <span aria-hidden>✕</span>
      </button>
    </div>
  )
}
